/**
 * ClosureRemarks Component
 * 
 * A comprehensive accordion-based component for managing training request closure remarks
 * and approval workflow. Displays revision history, approval status, and provides
 * interactive forms for approvers to add comments and make approval decisions.
 * 
 * @component
 * @author Welding Management System
 * @version 1.0.0
 * 
 * @param {Object} props - Component props
 * @param {Object} props.rowData - Training request data object
 * @param {string} props.rowData.SK - Unique identifier for the training request
 * @param {string} props.rowData.wld_TrainingReqRequesterName - Name of the requester
 * @param {string} props.rowData.wld_TrainingReqDate - Request creation date
 * @param {string} props.rowData.wld_status - Current status of the request
 * @param {string} props.calledFrom - Context from which component is called ("Approval" | "Initiated")
 * @param {Function} props.callReload - Callback to reload parent component data
 * @param {Function} props.close - Callback to close the modal/component
 * 
 * @example
 * <ClosureRemarks
 *   rowData={trainingRequestData}
 *   calledFrom="Approval"
 *   callReload={() => fetchData()}
 *   close={(reload, action) => handleClose(reload, action)}
 * />
 */
import { useEffect, useState } from "react";
import {
  getReqByType,
  updateTrainingRequest,
} from "../../../../Services/trainingRequisition-service";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Image,
  ListGroup,
  Row,
} from "react-bootstrap";
import Track from "../../../../icons/Track.svg";
import DownArrow from "../../../../icons/DownArrow.svg";
import { isAlphanumericWithSpecialChars } from "../../../../functions/validations";
import { showSuccess } from "../../ui/notifications/Toaster";
import { getInitials } from "../../../../functions/welding-functions";

const ClosureRemarks = (props) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  /** @type {Array} Array of closure remarks objects for different revisions */
  const [closureRemarksObj, setClosureRemarksObj] = useState([]);
  
  /** @type {Object} Controls which accordion item is expanded */
  const [collapseState, setCollapseState] = useState({});
  
  /** @type {string} Alert message for user notifications */
  const [alertMessage, setAlertMessage] = useState("");
  
  /** @type {string} Current approval status ("Approved" | "Rejected") */
  const [approvalStatus, setApprovalStatus] = useState("");
  
  /** @type {boolean} Controls if radio buttons are disabled */
  const [isRadioDisabled, setIsRadioDisabled] = useState(true);
  
  /** @type {Object} Current closure remarks data being edited */
  const [closureRemarksData, setClosureRemarksData] = useState();
  
  /** @type {boolean} Controls form visibility */
  const [showForm, setShowForm] = useState(false);

  // ============================================================================
  // CONFIGURATION CONSTANTS
  // ============================================================================
  
  /** 
   * Approval level configuration
   * Defines the approval hierarchy and designations
   */
  const levelCount = [
    { levelName: "Final", approverDesignation: "Welding Engineer" },
  ];

  /** 
   * Form validation error state
   * Tracks validation errors for form fields
   */
  const [error, setError] = useState({
    wld_TrainingReqApproverFinalComment: {},
  });

  // const getAllRecords = async () => {
  //   let tmpObj = { fieldValue: props.rowData.SK, flag: "reqNumber" };
  //   const Records = await getReqByType(tmpObj);
  //   if (Records?.length > 0) {
  //     setClosureRemarksObj(Records);

  //     let currRevision = Records.filter(
  //       (item) => item.RevisionNo === props.rowData.RevisionNo
  //     );
  //     if (currRevision?.length > 0) {
  //       SetClosureRemarksData(currRevision[0]);
  //     }

  //     setCollapse({ activeItem: props.rowData.RevisionNo });
  //   }
  // };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  /**
   * Handles accordion collapse/expand functionality
   * Toggles the active accordion item or collapses if already active
   * 
   * @param {number} collapsedItem - Index of the accordion item to toggle
   */
  const handleCollapsible = (collapsedItem) => {
    setCollapseState({
      activeItem: collapseState.activeItem === collapsedItem ? -1 : collapsedItem,
    });
  };

  /**
   * Handles input field changes for closure remarks
   * Updates the closure remarks data state with new values
   * 
   * @param {Event} e - Input change event
   */
  const handleChange = (e) => {
    setClosureRemarksData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  /**
   * Handles radio button changes for approval status
   * Enables form submission and updates approval status
   * 
   * @param {Event} e - Radio button change event
   */
  const handleRadioChange = (e) => {
    setIsRadioDisabled(false);
    setApprovalStatus(e.target.value);
    setClosureRemarksData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  const validateData = () => {
    const err = {};
    let isError = false;
    
    if (!closureRemarksData?.wld_TrainingReqApproverFinalComment?.trim()) {
      isError = true;
      err.wld_TrainingReqApproverFinalComment = {
        status: true,
        message: "Comment is required.",
      };
    }
    
    setError((prevState) => ({
      ...prevState,
      ...err,
    }));
    
    return isError;
  };

  const getAllRecords = async () => {
    try {
      const tmpObj = { fieldValue: props.rowData?.SK, flag: "reqNumber" };
      const Records = await getReqByType(tmpObj);
      
      if (Records?.length > 0) {
        setClosureRemarksObj(Records);
        
        const latestRevision = Math.max(...Records.map((r) => r.RevisionNo || 0));
        const newRevision = Records.find((r) => r.RevisionNo === latestRevision);
        
        if (newRevision) {
          setClosureRemarksData(newRevision);
          setCollapseState({ activeItem: latestRevision });
        }
      }
    } catch (error) {
      console.error('Error fetching records:', error);
      setClosureRemarksObj([]);
    }
  };

  const handleSubmit = async (flag) => {
    try {
      let valid = false;
      if (flag !== "Delete") {
        valid = validateData();
      }
      
      if (!valid) {
        if (!closureRemarksData) {
          console.error('No closure remarks data available');
          return;
        }
        
        const trainingObj = { ...props.rowData };
        const updatedData = { ...closureRemarksData };

        // Get current WE user info from session storage
        const currentUserId = sessionStorage.getItem("User");           // "sharshp"
        const currentUserName = sessionStorage.getItem("UserName");     // "Harsh"
        const currentUserEmail = sessionStorage.getItem("EmailId");     // "sharshp@godrej.com"
        
        console.log("Current WE approver:", {
          userId: currentUserId,
          userName: currentUserName, 
          userEmail: currentUserEmail
        });

        if (flag === "Delete") {
          updatedData.wld_status = "Deleted";
          updatedData.wld_pendingStatus = "Deleted";
          updatedData.wld_TrainingReqPendingWith = "system";
        } else {
          updatedData.wld_TrainingReqApproverFinalCreatedOn = new Date().toLocaleDateString("en-GB");

          if (approvalStatus === "Rejected") {//Rejected by approver
            updatedData.wld_status = "Rejected";
            updatedData.wld_pendingStatus = "Rejected";
            updatedData.wld_TrainingReqPendingWith = props.rowData?.wld_TrainingReqRequesterEmailId;
            
            // Add WE approver information for rejection
            updatedData.wld_TrainingReqApproverFinal = currentUserName;        // "Harsh"
            updatedData.wld_TrainingReqApproverFinalEmailId = currentUserEmail; // "sharshp@godrej.com"
            console.log("REJECTION - Adding WE approver info:", currentUserName, currentUserEmail);
            
          } else if (approvalStatus === "Approved") {
            updatedData.wld_status = "Approved";
            updatedData.wld_pendingStatus = "Approved";
            updatedData.wld_TrainingReqPendingWith = "system";
            
            // Add WE approver information for approval
            updatedData.wld_TrainingReqApproverFinal = currentUserName;        // "Harsh"
            updatedData.wld_TrainingReqApproverFinalEmailId = currentUserEmail; // "sharshp@godrej.com"
            console.log("APPROVAL - Adding WE approver info:", currentUserName, currentUserEmail);
            
          } else if (props.rowData?.wld_status === "Rejected") { //Saved after Rejection by initiator
            console.log('🔄 [ClosureRemarks] RESUBMISSION: Rejected request being resubmitted');
            console.log('🔄 [ClosureRemarks] Current context:', props.calledFrom);
            console.log('🔄 [ClosureRemarks] Current revision data:', updatedData);
            
            // CRITICAL FIX: During resubmission from "Initiated" context,
            // we should NOT update any existing revision records
            // Only the main training record should be updated to "Initiated"
            // The backend will create a new revision automatically
            
            if (props.calledFrom === "Initiated") {
              console.log('⚠️ [ClosureRemarks] RESUBMISSION from Initiated context - skipping revision update');
              console.log('⚠️ [ClosureRemarks] Only updating main record, preserving all revision history');
              
              // For resubmission, only update the main training record
              // Do NOT send any closure remarks data to avoid updating existing revisions
              const mainRecordUpdate = {
                ...trainingObj,
                wld_status: "Initiated",
                wld_pendingStatus: "ApproverFinal",
                wld_TrainingReqPendingWith: props.rowData?.wld_TrainingReqApproverFinalEmailId || "NA",
                RevisionNo: (props.rowData?.RevisionNo || 0) + 1
              };
              
              console.log('🔄 [ClosureRemarks] Resubmission - Main record update only:', mainRecordUpdate);
              
              // Send only the main record update, no closure remarks data
              const obj = { closureRemarksData: null, trainingObj: mainRecordUpdate };
              console.log('📤 [ClosureRemarks] Sending resubmission request (no closure update):', obj);
              
              await updateTrainingRequest(obj);
              showSuccess('Training Request has been resubmitted successfully');
              
              console.log('🔄 [ClosureRemarks] Refreshing records after resubmission');
              await getAllRecords();
              props.callReload?.();
              setShowForm(false);
              return; // Exit early to prevent any revision updates
            }
            
            // This path should not be reached during normal resubmission
            console.log('⚠️ [ClosureRemarks] Unexpected resubmission path - updating revision data');
            updatedData.wld_status = "Initiated";
            updatedData.wld_pendingStatus = "ApproverFinal";
            updatedData.wld_TrainingReqPendingWith = props.rowData?.wld_TrainingReqApproverFinalEmailId || "NA";
          }
        }

        console.log("Final closure remarks data being sent:", updatedData);
        const obj = { closureRemarksData: updatedData, trainingObj };
        await updateTrainingRequest(obj);
        showSuccess('Training Request has been updated successfully');

        await getAllRecords();
        props.callReload?.();
        setShowForm(false);
      }
    } catch (error) {
      console.error('Error updating training request:', error);
    }
  };

  function handleCancel() {
    props.close(true, "Cancel");
  }

  useEffect(() => {
    getAllRecords();
  }, [showForm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAlertMessage("");
    }, 1000);
    return () => clearTimeout(timer);
  }, [alertMessage]);

  // ============================================================================
  // COMPONENT RENDER
  // ============================================================================
  
  return (
    <>
      {/* 
        MAIN ACCORDION CONTAINER
        Displays revision history and approval workflow for training requests
        Each accordion item represents a different revision of the request
      */}
      <Accordion defaultActiveKey="1" style={{ marginTop: "20px" }}>
        {closureRemarksObj?.map((value, index) => (
          <Accordion.Item
            eventKey={index}
            style={{ backgroundColor: "white", marginBottom: "2rem" }}
            key={`closure-${value.SK || value.RevisionNo}`}
          >
            {/* 
              ACCORDION HEADER
              Shows revision number with track icon and expand/collapse arrow
            */}
            <Accordion.Header
              className={
                collapseState?.activeItem === index
                  ? `dispaly-none accordion-header`
                  : `accordion-header`
              }
              onClick={() => handleCollapsible(index)}
            >
              {/* Track Icon */}
              <Image src={Track} alt="Track" className="profile-initials left43" />
              
              {/* Revision Label */}
              <Form.Label
                className="remarkLabel size-20"
                style={{ position: "relative", left: "63px" }}
              >
                Track Report - Rev.{index}
              </Form.Label>
              
              {/* Expand/Collapse Arrow */}
              <Image
                src={DownArrow}
                alt="DownArrow"
                className="blue-arrow downarrow"
                style={{ position: "relative", left: "45px" }}
              />
            </Accordion.Header>
            {/* 
              ACCORDION BODY
              Contains the detailed workflow information and approval forms
            */}
            <Accordion.Body
              className={
                collapseState?.activeItem !== index ? `dispaly-none` : `show-block`
              }
            >
              <Form>
                <ListGroup as="ol">
                  {/* 
                    REQUESTER INFORMATION SECTION
                    Displays the original requester's details and initiation status
                  */}
                  <ListGroup.Item
                    as="li"
                    className="d-flex justify-content-between align-items-start"
                    style={{
                      borderLeft: "none",
                      borderTop: "none",
                      borderRight: "none",
                    }}
                  >
                    {/* Requester Avatar with Initials */}
                    <div className="profile-initialsClosure" style={{ marginLeft: "-0.4%" }}>
                      {getInitials(props.rowData.wld_TrainingReqRequesterName)}
                    </div>
                    
                    {/* Requester Name and Designation */}
                    <div className="ms-5 me-auto">
                      <div className="fw-bold">
                        {props.rowData.wld_TrainingReqRequesterName}
                      </div>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "grey",
                          position: "relative",
                          bottom: "7px",
                        }}
                      >
                        Engineer
                      </span>
                    </div>
                    
                    {/* Request Date */}
                    <span
                      style={{
                        fontSize: "12px",
                        color: "grey",
                        paddingRight: "32px",
                      }}
                    >
                      {props.rowData.wld_TrainingReqDate}
                    </span>
                    
                    {/* Status Badge */}
                    <Badge bg="light" style={{ color: "black" }}>
                      Initiated
                    </Badge>
                  </ListGroup.Item>

                  {/* 
                    FINAL APPROVER SECTION
                    Displays approver information and interactive approval forms
                    Maps through levelCount to support multiple approval levels
                  */}
                  {levelCount.map((ind) => (
                    <ListGroup.Item
                      key={`level-${ind.levelName}-${value.SK || value.RevisionNo}`}
                      as="li"
                      className="d-flex justify-content-between align-items-start"
                      style={{
                        borderLeft: "none",
                        borderTop: "none",
                        borderRight: "none",
                      }}
                    >
                      {/* Approver Avatar with Initials */}
                      <div className="profile-initialsClosure" style={{ marginLeft: "-0.4%" }}>
                        {getInitials(value.wld_TrainingReqApproverFinal)}
                      </div>
                      
                      {/* Approver Information and Forms Container */}
                      <div className="ms-5 me-auto" style={{ width: "27%" }}>
                        {/* Approver Name and Designation */}
                        <div className="fw-bold">{value.wld_TrainingReqApproverFinal}</div>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "grey",
                            position: "relative",
                            bottom: "7px",
                          }}
                        >
                          {ind.approverDesignation}
                        </span>
                        <br />
                        <br />
                        {/* 
                          CONDITIONAL APPROVAL FORM
                          Shows interactive form only when:
                          - Called from Approval context
                          - Status is pending with ApproverFinal
                          - Overall status is Initiated
                        */}
                        {
                          props.calledFrom === "Approval" &&
                            value.wld_pendingStatus === "ApproverFinal" &&
                            value.wld_status === "Initiated"
                            ? (
                              <>
                                {/* 
                                  COMMENT INPUT SECTION
                                  Allows approver to add comments for their decision
                                */}
                                <Form.Group>
                                  <div
                                    className="g-input"
                                    style={{
                                      width: "400%",
                                      position: "relative",
                                      bottom: "13px",
                                    }}
                                  >
                                    <input
                                      id="wld_TrainingReqApproverFinalComment"
                                      name="wld_TrainingReqApproverFinalComment"
                                      type="text"
                                      className={
                                        error.wld_TrainingReqApproverFinalComment.status
                                          ? "placeHolderInput inputText text-field-with-error"
                                          : "placeHolderInput inputText"
                                      }
                                      placeholder=" "
                                      onChange={handleChange}
                                      style={{ width: "80%" }}
                                      onInput={(e) => {
                                        let err = isAlphanumericWithSpecialChars(e.target.value);
                                        setError((prevState) => ({
                                          ...prevState,
                                          wld_TrainingReqApproverFinalComment: err,
                                        }));
                                      }}
                                    />
                                    <Form.Label htmlFor="wld_TrainingReqApproverFinalComment">
                                      Comments
                                    </Form.Label>
                                  </div>
                                  {error.wld_TrainingReqApproverFinalComment.status && (
                                    <Form.Text
                                      className="text-danger"
                                      style={{
                                        position: "relative",
                                        bottom: "20px",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {error.wld_TrainingReqApproverFinalComment.message}
                                    </Form.Text>
                                  )}
                                </Form.Group>

                                {/* 
                                  ========================================================
                                  APPROVAL/REJECTION RADIO BUTTONS
                                  ========================================================
                                  
                                  PURPOSE: Allows approver to select their decision
                                  
                                  BEHAVIOR:
                                  - Selecting either option calls handleRadioChange()
                                  - Sets isRadioDisabled to false (enables Save button)
                                  - Updates approvalStatus state
                                  - Updates closureRemarksData with selected value
                                  
                                  IMPACT ON SAVE BUTTON:
                                  - Initially Save button is DISABLED (isRadioDisabled = true)
                                  - After selection, Save button becomes ENABLED
                                */}
                                <div className="m-input" style={{ position: "relative", bottom: "15px" }}>
                                  <div className="d-flex">
                                    {/* 
                                      APPROVE OPTION
                                      Value: "Approved"
                                      Effect: Enables Save button, sets status to approved
                                    */}
                                    <input
                                      id="wld_TrainingReqApproverFinalStatus"
                                      type="radio"
                                      name="wld_TrainingReqApproverFinalStatus"
                                      value="Approved"
                                      onChange={handleRadioChange}
                                    />
                                    <span>&nbsp;Approve</span>
                                    
                                    {/* 
                                      REJECT OPTION
                                      Value: "Rejected"
                                      Effect: Enables Save button, sets status to rejected
                                    */}
                                    <div className="d-flex" style={{ position: "relative", left: "60px" }}>
                                      <input
                                        id="wld_TrainingReqApproverFinalStatus"
                                        type="radio"
                                        name="wld_TrainingReqApproverFinalStatus"
                                        value="Rejected"
                                        onChange={handleRadioChange}
                                      />
                                      <span>&nbsp;Reject</span>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              /* 
                                READ-ONLY COMMENT DISPLAY
                                Shows existing comments when not in approval mode
                              */
                              <i style={{ fontSize: "12px", color: "grey" }}>
                                {value.wld_TrainingReqApproverFinalComment}
                              </i>
                            )}
                      </div>
                      {/* Approval Date */}
                      <span
                        style={{
                          fontSize: "12px",
                          color: "grey",
                          paddingRight: "32px",
                        }}
                      >
                        {value.wld_TrainingReqApproverFinalCreatedOn}
                      </span>
                      
                      {/* 
                        DYNAMIC STATUS BADGE
                        Shows approval status with appropriate colors
                        Uses IIFE to calculate colors based on status
                      */}
                      {(() => {
                        const status = value.wld_TrainingReqApproverFinalStatus;
                        
                        // Color mapping functions
                        const getColor = () => {
                          if (status === "Approved") return "var(--godrej-green-alt)";
                          if (status === "In Progress") return "var(--godrej-orange)";
                          return "var(--godrej-red)";
                        };
                        
                        const getBackground = () => {
                          if (status === "Approved") return "var(--godrej-green-bg)";
                          if (status === "In Progress") return "var(--godrej-orange-bg)";
                          return "var(--godrej-red-bg)";
                        };
                        
                        const getBg = () => {
                          if (status === "Approved") return "sucessapproved";
                          if (status === "In Progress") return "inprogressapproved";
                          return "rejctedapproved";
                        };
                        
                        return (
                          <Badge
                            style={{
                              position: "relative",
                              color: getColor(),
                              background: getBackground(),
                            }}
                            bg={getBg()}
                          >
                            {status}
                          </Badge>
                        );
                      })()}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Form>
              <Row style={{ alignContent: "right" }}>
                <Col style={{ padding: "0" }}>
                  <div className="container history-box"></div>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      {/* 
        ACTION BUTTONS SECTION - SIMPLIFIED LOGIC
        
        BUTTON VISIBILITY RULES:
        1. Cancel: Always visible
        2. Delete: Initiated context + Initiated status
        3. Save: Initiated context + Rejected status OR Approval context + Initiated status
      */}
      <Row>
        {/* 
          ALERT MESSAGE DISPLAY
          Shows temporary notifications to user
          Visible when: alertMessage state is not empty
          Auto-hides after 1 second (controlled by useEffect)
        */}
        {alertMessage && (
          <Alert className="alertBox" variant="warning">
            {alertMessage}
          </Alert>
        )}
        
        <div>
          {/* 
            CANCEL BUTTON - ALWAYS VISIBLE
            Purpose: Allows user to exit without saving changes
            Visibility: Always shown regardless of status or context
            Action: Calls handleCancel() which triggers props.close(true, "Cancel")
            Position: Left side of button row
          */}
          <div className="float-start m-1 visible">
            <Button
              variant="outline-primary"
              onClick={handleCancel}
              style={{
                width: "100px",
                height: "38px",
                position: "relative",
                left: "10px",
                top: "10px",
              }}
            >
              Cancel
            </Button>
          </div>
          
          {/* 
            SIMPLIFIED ACTION BUTTONS LOGIC
            
            OUTER CONDITION - Shows buttons when called from "Initiated" context AND:
            ✓ Status is "Initiated" OR "Rejected" (actionable states)
            ✓ Status is NOT "Deleted" or "Approved" (final states)
          */}
          {props.calledFrom === "Initiated" && 
           (props.rowData.wld_status === "Initiated" || props.rowData.wld_status === "Rejected") && (
              <div className="float-end m-1">
                {/* 
                  DELETE BUTTON - Visible when status is "Initiated" (not rejected yet)
                  Purpose: Allow requester to delete their pending request
                */}
                {props.rowData.wld_status === "Initiated" ? (
                  <Button
                    className="btn-add-custom"
                    onClick={() => handleSubmit("Delete")}
                    style={{ width: "100px", height: "38px" }}
                  >
                    Delete
                  </Button>
                ) : (
                  /* 
                    SAVE BUTTON - Visible when status is "Rejected"
                    Purpose: Allow requester to resubmit after rejection
                  */
                  <Button
                    className="btn-add-custom"
                    onClick={() => handleSubmit("Save")}
                    style={{ width: "100px", height: "38px" }}
                  >
                    Save
                  </Button>
                )}
              </div>
            )}
            
          {/* 
            APPROVAL CONTEXT BUTTONS
            Shows Save button for approvers to make decisions
          */}
          {props.calledFrom === "Approval" && 
           props.rowData.wld_status === "Initiated" && (
              <div className="float-end m-1">
                <Button
                  className="btn-add-custom"
                  onClick={() => handleSubmit("Save")}
                  style={{ width: "100px", height: "38px" }}
                  disabled={isRadioDisabled} // Enabled only after radio selection
                >
                  Save
                </Button>
              </div>
            )}
        </div>
      </Row>
    </>
  );
};

export default ClosureRemarks;